import { type FormEvent, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  type CarrierRateBandRow,
  type CarrierServiceAreaRow,
  useCarrierSurcharges,
  useCreateCarrierRateBand,
  useUpdateCarrierRateBand,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Structured rate-band editor.
 *
 * Replaces a raw-JSON textarea the carrier had to hand-edit (id and timestamps
 * included) with typed fields and named lanes. The server validates the same
 * rules it enforces for EZX staff — rate floor, bracket order, no overlaps —
 * so anything this form lets through, it lets through for a reason.
 */

/** Dollars in the UI, cents on the wire. Nobody prices in cents. */
const toCents = (dollars: string): number =>
  Math.round(Number.parseFloat(dollars || "0") * 100);
const toDollars = (cents: number | null | undefined): string =>
  cents == null ? "" : (cents / 100).toFixed(2);
const num = (v: string | number | null | undefined): string =>
  v == null ? "" : String(v);

export interface RateBandDefaults {
  fromServiceAreaId?: string;
  toServiceAreaId?: string;
  weightFromKg?: number;
  weightToKg?: number;
}

/**
 * Show the consequence.
 *
 * A carrier types a number into a box. Nothing on this screen used to say what
 * that number DOES — not what they keep, not what the customer pays. The one
 * question they actually have goes unanswered while they answer it.
 *
 * The fee math mirrors the server (platform-fee.helper.ts → computeFeeCents).
 * It is one line, deliberately, so it can't drift far; and it's a *preview* —
 * the server remains the source of truth for what is actually charged.
 */
function EarningsPreview({
  baseRate,
  fee,
  currencyCode,
}: {
  baseRate: string;
  fee: { percent: number | null; flatCents: number | null } | null;
  currencyCode: string;
}) {
  const rateCents = Math.round((Number.parseFloat(baseRate) || 0) * 100);

  if (rateCents <= 0) return null;

  if (currencyCode !== "CAD") {
    return (
      <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
        Earnings preview is available for CAD bands. The quote engine converts
        {` ${currencyCode || "this currency"}`} using its live FX rate.
      </div>
    );
  }

  if (!fee || fee.percent === null || fee.flatCents === null) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
        <span className="font-medium text-destructive">
          EZX has no fee set for this service
        </span>
        <p className="text-muted-foreground">
          It can&apos;t be quoted to customers until they do. Contact EZX.
        </p>
      </div>
    );
  }

  const feeCents = Math.round(rateCents * (fee.percent / 100)) + fee.flatCents;
  const customerCents = rateCents + feeCents;
  const money = (c: number) => `$${(c / 100).toFixed(2)}`;

  return (
    <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span>
          You keep{" "}
          <span className="font-semibold tabular-nums">{money(rateCents)}</span>
        </span>
        <span className="text-muted-foreground">
          EZX fee <span className="tabular-nums">{money(feeCents)}</span>{" "}
          <span className="text-xs">
            ({fee.percent}% + {money(fee.flatCents)})
          </span>
        </span>
        <span>
          Customer pays{" "}
          <span className="font-semibold tabular-nums">
            {money(customerCents)}
          </span>
        </span>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Before tax. Your rate is what you earn; the fee is EZX&apos;s.
      </p>
    </div>
  );
}

export function EditRateBandDialog({
  open,
  onOpenChange,
  band,
  carrierServiceId,
  serviceAreas,
  defaults,
  serviceLabel,
  dispatchCenterLabel,
  dimFactorLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = creating a new band. */
  band: CarrierRateBandRow | null;
  /** Which service the new band hangs off (create mode). */
  carrierServiceId: string;
  serviceAreas: CarrierServiceAreaRow[];
  /** Optional lane and bracket seeded by an empty matrix cell. */
  defaults?: RateBandDefaults;
  /** Read-only scope context retained inside the mobile Sheet. */
  serviceLabel?: string;
  dispatchCenterLabel?: string;
  dimFactorLabel?: string;
}) {
  const create = useCreateCarrierRateBand();
  const update = useUpdateCarrierRateBand();
  // The EZX fee on this service, resolved server-side. Needed to answer the only
  // question a carrier actually has while typing a rate: what do I keep, and
  // what does my customer see?
  const surcharges = useCarrierSurcharges();
  const pending = create.isPending || update.isPending;
  const isEdit = band !== null;
  const submitLock = useRef(false);

  const [fromAreaId, setFromAreaId] = useState("");
  const [toAreaId, setToAreaId] = useState("");
  const [weightFrom, setWeightFrom] = useState("0");
  const [weightTo, setWeightTo] = useState("5");
  const [baseRate, setBaseRate] = useState("");
  const [perKg, setPerKg] = useState("");
  const [anchorWeight, setAnchorWeight] = useState("");
  const [rushHour, setRushHour] = useState("");
  const [currencyCode, setCurrencyCode] = useState("CAD");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFromAreaId(
      band?.fromServiceArea.id ?? defaults?.fromServiceAreaId ?? ""
    );
    setToAreaId(band?.toServiceArea.id ?? defaults?.toServiceAreaId ?? "");
    setWeightFrom(num(band?.weightFromKg ?? defaults?.weightFromKg) || "0");
    setWeightTo(num(band?.weightToKg ?? defaults?.weightToKg) || "5");
    setBaseRate(toDollars(band?.baseRateCents));
    setPerKg(toDollars(band?.perKgCents));
    setAnchorWeight(num(band?.anchorWeightKg));
    setRushHour(toDollars(band?.rushHourSurchargeCents));
    setCurrencyCode(band?.currencyCode ?? "CAD");
  }, [
    open,
    band,
    defaults?.fromServiceAreaId,
    defaults?.toServiceAreaId,
    defaults?.weightFromKg,
    defaults?.weightToKg,
  ]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitLock.current) return;
    setError(null);

    if (!fromAreaId || !toAreaId) {
      setError("Pick both an origin and destination service area.");
      return;
    }

    const weightFromValue = Number.parseFloat(weightFrom);
    const weightToValue = Number.parseFloat(weightTo);
    const baseRateValue = Number.parseFloat(baseRate);
    const perKgValue = perKg ? Number.parseFloat(perKg) : null;
    const anchorWeightValue = anchorWeight
      ? Number.parseFloat(anchorWeight)
      : null;
    const rushHourValue = rushHour ? Number.parseFloat(rushHour) : null;
    if (
      !Number.isFinite(weightFromValue) ||
      !Number.isFinite(weightToValue) ||
      weightFromValue < 0 ||
      weightToValue <= weightFromValue
    ) {
      setError("Weight to must be greater than weight from.");
      return;
    }
    if (!Number.isFinite(baseRateValue) || baseRateValue < 0) {
      setError("Enter a valid non-negative base rate.");
      return;
    }
    if (
      perKgValue !== null &&
      (!Number.isFinite(perKgValue) || perKgValue < 0)
    ) {
      setError("Enter a valid non-negative per-kg rate.");
      return;
    }
    if ((!perKgValue || perKgValue === 0) && toCents(baseRate) < 100) {
      setError(
        "A flat rate must be at least $1.00. Add a per-kg rate for slope pricing."
      );
      return;
    }
    if (
      anchorWeightValue !== null &&
      (!Number.isFinite(anchorWeightValue) || anchorWeightValue < 0)
    ) {
      setError("Enter a valid non-negative anchor weight.");
      return;
    }
    if (
      rushHourValue !== null &&
      (!Number.isFinite(rushHourValue) || rushHourValue < 0)
    ) {
      setError("Enter a valid non-negative rush-hour amount.");
      return;
    }
    const normalizedCurrency = currencyCode.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(normalizedCurrency)) {
      setError("Currency must be a three-letter ISO code, such as CAD.");
      return;
    }

    const payload = {
      carrierServiceId: band?.carrierServiceId ?? carrierServiceId,
      fromServiceAreaId: fromAreaId,
      toServiceAreaId: toAreaId,
      weightFromKg: weightFromValue,
      weightToKg: weightToValue,
      baseRateCents: toCents(baseRate),
      perKgCents: perKg ? toCents(perKg) : null,
      anchorWeightKg: anchorWeightValue,
      rushHourSurchargeCents: rushHour ? toCents(rushHour) : null,
      currencyCode: normalizedCurrency,
    };

    submitLock.current = true;
    try {
      if (isEdit) {
        await update.mutateAsync({ id: band.id, ...payload });
      } else {
        await create.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (err) {
      // Surface the server's actual complaint (overlap, floor, bracket) rather
      // than a generic failure — the guardrails are only useful if the carrier
      // can read them.
      const res = (
        err as { response?: { data?: { error?: string; details?: unknown } } }
      )?.response?.data;
      setError(res?.error ?? "Could not save the rate band. Try again.");
    } finally {
      submitLock.current = false;
    }
  };

  const serviceId = band?.carrierServiceId ?? carrierServiceId;
  const fee =
    surcharges.data?.platformFees.find(
      (f) => f.carrierServiceId === serviceId
    ) ?? null;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!pending && !submitLock.current) onOpenChange(nextOpen);
      }}
      title={isEdit ? "Edit rate band" : "Add rate band"}
      presentation="responsive-sheet"
      className="md:max-w-4xl"
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border bg-muted/30 p-3 text-sm sm:grid-cols-4">
          <RateContextValue label="Service" value={serviceLabel ?? serviceId} />
          <RateContextValue
            label="Dispatch Center"
            value={dispatchCenterLabel ?? "-"}
          />
          <RateContextValue label="Dim factor" value={dimFactorLabel ?? "-"} />
          <RateContextValue
            label="Effective state"
            value="Applies immediately"
          />
        </dl>

        <EarningsPreview
          baseRate={baseRate}
          fee={fee}
          currencyCode={currencyCode.trim().toUpperCase()}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="rb-from" className="block text-sm font-medium">
              From
            </label>
            <Select value={fromAreaId} onValueChange={setFromAreaId}>
              <SelectTrigger id="rb-from" size="sm">
                <SelectValue placeholder="Pick origin area" />
              </SelectTrigger>
              <SelectContent>
                {serviceAreas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rb-to" className="block text-sm font-medium">
              To
            </label>
            <Select value={toAreaId} onValueChange={setToAreaId}>
              <SelectTrigger id="rb-to" size="sm">
                <SelectValue placeholder="Pick destination area" />
              </SelectTrigger>
              <SelectContent>
                {serviceAreas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    {area.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label htmlFor="rb-wfrom" className="block text-sm font-medium">
              Package billable weight from (kg)
            </label>
            <Input
              id="rb-wfrom"
              type="number"
              min="0"
              step="any"
              required
              value={weightFrom}
              onChange={(e) => setWeightFrom(e.target.value)}
              disabled={pending}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rb-wto" className="block text-sm font-medium">
              Package billable weight to (kg)
            </label>
            <Input
              id="rb-wto"
              type="number"
              min="0"
              step="any"
              required
              value={weightTo}
              onChange={(e) => setWeightTo(e.target.value)}
              disabled={pending}
            />
            <p className="text-xs text-muted-foreground">
              Upper bound is exclusive.
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="rb-base" className="block text-sm font-medium">
              Base / shipment ($)
            </label>
            <Input
              id="rb-base"
              type="number"
              min="0"
              step="0.01"
              className="h-11"
              required
              value={baseRate}
              onChange={(e) => setBaseRate(e.target.value)}
              disabled={pending}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rb-perkg" className="block text-sm font-medium">
              Per kg ($)
            </label>
            <Input
              id="rb-perkg"
              type="number"
              min="0"
              step="0.01"
              className="h-11"
              placeholder="Optional"
              value={perKg}
              onChange={(e) => setPerKg(e.target.value)}
              disabled={pending}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rb-anchor" className="block text-sm font-medium">
              Anchor weight (kg)
            </label>
            <Input
              id="rb-anchor"
              type="number"
              min="0"
              step="any"
              placeholder="Weight from"
              value={anchorWeight}
              onChange={(e) => setAnchorWeight(e.target.value)}
              disabled={pending}
            />
            <p className="text-xs text-muted-foreground">
              Per-kg pricing starts above this weight.
            </p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rb-rush" className="block text-sm font-medium">
              Rush hour ($)
            </label>
            <Input
              id="rb-rush"
              type="number"
              min="0"
              step="0.01"
              className="h-11"
              placeholder="Optional"
              value={rushHour}
              onChange={(e) => setRushHour(e.target.value)}
              disabled={pending}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rb-currency" className="block text-sm font-medium">
              Currency
            </label>
            <Input
              id="rb-currency"
              className="uppercase"
              inputMode="text"
              maxLength={3}
              value={currencyCode}
              onChange={(event) =>
                setCurrencyCode(event.target.value.toUpperCase())
              }
              disabled={pending}
            />
          </div>
        </div>

        {error && (
          <p
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <div className="sticky bottom-0 grid grid-cols-2 gap-2 border-t bg-background pt-4">
          <Button
            type="button"
            variant="outline"
            size="touch"
            onClick={() => {
              if (!pending && !submitLock.current) onOpenChange(false);
            }}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button type="submit" size="touch" disabled={pending}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save changes" : "Add band"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RateContextValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate" title={value}>
        {value}
      </dd>
    </div>
  );
}
